import { useState } from "react";
import { format } from "date-fns";
import {
  Clock,
  Check,
  Edit,
  Trash2,
  Phone,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import { useQuotes } from "../../Hooks/useQuotes";
import { useCompanyOffers } from "../../Hooks/useCompanyOffers";
import { toast } from "react-hot-toast";
import { canAccess } from "../../utils/permissions";
import DeleteOfferConfirmationModal from "./components/DeleteOfferConfirmationModal";

export default function OffersManagement() {
  const [activeTab, setActiveTab] = useState("all");
  const [showNewOfferModal, setShowNewOfferModal] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState("");
  const [price, setPrice] = useState("");
  const [days, setDays] = useState("");
  const [message, setMessage] = useState("");
  const [agreementType, setAgreementType] = useState("signature");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const [editPrice, setEditPrice] = useState("");
  const [editDays, setEditDays] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editAgreementType, setEditAgreementType] = useState("signature");
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { quotes: openQuotes } = useQuotes();
  const {
    allOffers,
    acceptedOffers,
    loadingAll,
    createOffer,
    isCreating,
    updateOffer,
    deleteOffer,
    isDeleting,
  } = useCompanyOffers();

  const offersToShow = activeTab === "all" ? allOffers : acceptedOffers;
  const isLoading = activeTab === "all" ? loadingAll : false;

  const canSend = canAccess('send_offer');
  const canEdit = canAccess('edit_offer');
  const canDelete = canAccess('delete_offer');

  if (!canAccess("offers_management")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center text-sm">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to manage offers.
        </p>
      </div>
    );
  }



  const handleCreateOffer = () => {
    if (!selectedQuoteId || !price || !days) {
      toast.error("Please fill all required fields");
      return;
    }

    createOffer(
      {
        quoteId: selectedQuoteId,
        data: {
          price: parseFloat(price),
          estimated_days: parseInt(days),
          message: message || null,
          agreement_type: agreementType,
          terms_and_conditions: "50% deposit required upon acceptance.",
        },
      },
      {
        onSuccess: () => {
          setShowNewOfferModal(false);
          setSelectedQuoteId("");
          setPrice("");
          setDays("");
          setMessage("");
          resetNewForm();
        },
      }
    );
  };

  const getStatusColor = (status) => {
    return status === "accepted"
      ? "bg-emerald-100 text-emerald-700"
      : status === "pending"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-gray-100 text-gray-600";
  };

  const resetNewForm = () => {
    setSelectedQuoteId("");
    setPrice("");
    setDays("");
    setMessage("");
    setAgreementType("signature");
  };

  const openEditModal = (offer) => {
    if (!canAccess("edit_offer")) {
      toast.error("You don't have permission to edit offers");
      return;
    }
    setEditingOffer(offer);
    setEditPrice(offer.price);
    setEditDays(offer.estimated_days || "");
    setEditMessage(offer.message || "");
    setEditAgreementType(offer.agreement_type || "signature");
    setShowEditModal(true);
  };

  const handleUpdateOffer = () => {
    if (!editPrice || !editDays) return toast.error("Price and days required");

    updateOffer(
      {
        offerId: editingOffer.id,
        data: {
          price: parseFloat(editPrice),
          estimated_days: parseInt(editDays),
          message: editMessage || null,
          agreement_type: editAgreementType,
        },
      },
      {
        onSuccess: () => {
          toast.success("Offer updated successfully!");
          setShowEditModal(false);
          setEditingOffer(null);
        },
      }
    );
  };

  const handleDeleteClick = (offer) => {
    if (!canAccess("delete_offer")) {
      toast.error("You don't have permission to delete offers");
      return;
    }
    setOfferToDelete(offer);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (offerToDelete) {
      deleteOffer(offerToDelete.id, {
        onSuccess: () => {
          setShowDeleteModal(false);
          setOfferToDelete(null);
        },
      });
    }
  };

  return (
    <section className="min-h-screen">
      <div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Offers</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage all offers you've sent
                </p>
              </div>

            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setActiveTab("all")}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${activeTab === "all"
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                All Offers ({allOffers.length})
              </button>
              <button
                onClick={() => setActiveTab("accepted")}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${activeTab === "accepted"
                  ? "bg-emerald-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
              >
                Accepted ({acceptedOffers.length})
              </button>
            </div>
          </div>

          <div className="p-6">
            {isLoading ? (
              <div className="text-center py-20">
                <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
              </div>
            ) : offersToShow.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">
                  No {activeTab === "accepted" ? "accepted" : ""} offers yet
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {offersToShow.map((offer) => {
                  const q = offer.quote;
                  const client = `${q.first_name} ${q.last_name}`.trim();
                  return (
                    <div
                      key={offer.id}
                      className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold">
                            {client[0]}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">
                              {client || "Client"}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {q.boat_make} {q.boat_length}ft • {q.service.name}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />{" "}
                                {format(
                                  new Date(offer.created_at),
                                  "MMM d, yyyy"
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <Phone className="w-3.5 h-3.5" /> {q.phone}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <span
                            className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(
                              offer.status
                            )}`}
                          >
                            {offer.status.toUpperCase()}
                          </span>
                          <p className="text-2xl font-bold text-gray-900 mt-2">
                            ${offer.price}
                          </p>

                        </div>
                      </div>

                      <div className="mt-4 flex justify-end gap-3">
                        {offer.status === "pending" && (
                          <>
                            {canAccess("edit_offer") && (
                              <button
                                onClick={() => openEditModal(offer)}
                                className="flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                              >
                                <Edit className="w-4 h-4" /> Edit Offer
                              </button>
                            )}
                            {canAccess("delete_offer") && (
                              <button
                                onClick={() => handleDeleteClick(offer)}
                                className="text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                              >
                                <Trash2 className="w-4 h-4" /> Delete
                              </button>
                            )}
                          </>
                        )}
                        {offer.status === "accepted" && (
                          <span className="text-emerald-600 font-bold flex items-center gap-1">
                            <Check className="w-5 h-5" /> Offer Confirmed
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Offer Modal */}
      {showNewOfferModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowNewOfferModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Create New Offer
            </h2>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Request
                </label>
                <select
                  value={selectedQuoteId}
                  onChange={(e) => setSelectedQuoteId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Choose a request...</option>
                  {openQuotes.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.first_name} {q.last_name} • {q.boat_make}{" "}
                      {q.boat_length}ft • {q.service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (USD)
                  </label>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="1750"
                    className="w-full px-4 py-3 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Days
                  </label>
                  <input
                    type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    placeholder="3"
                    className="w-full px-4 py-3 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agreement Type
                </label>
                <select
                  value={agreementType}
                  onChange={(e) => setAgreementType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white"
                >
                  <option value="signature">Signature</option>
                  <option value="text">Text</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Full survey including sea trial..."
                  className="w-full px-4 py-3 border rounded-lg resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => setShowNewOfferModal(false)}
                className="px-6 py-3 bg-gray-200 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOffer}
                disabled={isCreating}
                className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg font-bold flex items-center gap-2 disabled:opacity-70"
              >
                {isCreating ? <>Sending...</> : <>Send Offer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingOffer && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Edit Offer</h2>
              <button onClick={() => setShowEditModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium">
                  {editingOffer.quote.first_name} {editingOffer.quote.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  {editingOffer.quote.boat_make}{" "}
                  {editingOffer.quote.boat_length}ft •{" "}
                  {editingOffer.quote.service.name}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Price (USD)
                  </label>
                  <input
                    type="number"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Estimated Days
                  </label>
                  <input
                    type="number"
                    value={editDays}
                    onChange={(e) => setEditDays(e.target.value)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Agreement Type</label>
                <select
                  value={editAgreementType}
                  onChange={(e) => setEditAgreementType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 bg-white"
                >
                  <option value="signature">Signature (توقيع)</option>
                  <option value="text">Text (نص)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  rows={4}
                  value={editMessage}
                  onChange={(e) => setEditMessage(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-6 py-3 bg-gray-200 rounded-lg font-medium"
              >
                Cancel
              </button>
              {canAccess("edit_offer") ? (
                <button
                  onClick={handleUpdateOffer}
                  className="px-8 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-bold flex items-center gap-2"
                >
                  <Check className="w-5 h-5" /> Update Offer
                </button>
              ) : (
                <div className="text-sm text-gray-500 flex items-center">
                  You don't have permission to edit offers.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <DeleteOfferConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setOfferToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        offerDetails={offerToDelete}
        isLoading={isDeleting}
      />
    </section>
  );
}
